import React, { useState } from 'react';
import './AddPropertyModal.css';

const AddPropertyModal = ({ isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    propertyName: '',
    address: '',
    propertyValue: 0,
    purchasePrice: 0
  });

  const [errors, setErrors] = useState({});

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleValueChange = (field, value) => {
    // Remove non-numeric characters and convert to number
    const numericValue = parseFloat(value.replace(/[^0-9.-]/g, '')) || 0;
    handleInputChange(field, numericValue);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.propertyName.trim()) {
      newErrors.propertyName = 'Property name is required';
    }

    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    }

    if (formData.propertyValue <= 0) {
      newErrors.propertyValue = 'Property value must be greater than $0';
    }

    if (formData.purchasePrice < 0) {
      newErrors.purchasePrice = 'Purchase price cannot be negative';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validateForm()) {
      onSave(formData);
      onClose();
    }
  };

  const handleCancel = () => {
    setFormData({
      propertyName: '',
      address: '',
      propertyValue: 0,
      purchasePrice: 0
    });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleCancel}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Complete your information</h2>
          <button className="close-btn" onClick={handleCancel}>×</button>
        </div>
        
        <div className="modal-body">
          <form className="property-form">
            <div className="form-group">
              <label htmlFor="propertyName">Property name</label>
              <input
                type="text"
                id="propertyName"
                value={formData.propertyName}
                onChange={(e) => handleInputChange('propertyName', e.target.value)}
                className={`form-input ${errors.propertyName ? 'error' : ''}`}
                placeholder="Enter property name"
              />
              {errors.propertyName && <span className="error-message">{errors.propertyName}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="address">Address</label>
              <div className="input-with-icon">
                <input
                  type="text"
                  id="address"
                  value={formData.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  className={`form-input ${errors.address ? 'error' : ''}`}
                  placeholder="Enter property address"
                />
                <div className="dropdown-icon">▼</div>
              </div>
              {errors.address && <span className="error-message">{errors.address}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="propertyValue">Property value</label>
              <div className="currency-input">
                <span className="currency-symbol">$</span>
                <input
                  type="text"
                  id="propertyValue"
                  value={formData.propertyValue === 0 ? '' : formatCurrency(formData.propertyValue).replace('$', '')}
                  onChange={(e) => handleValueChange('propertyValue', e.target.value)}
                  className={`form-input ${errors.propertyValue ? 'error' : ''}`}
                  placeholder="0"
                />
              </div>
              {errors.propertyValue && <span className="error-message">{errors.propertyValue}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="purchasePrice">Purchase price</label>
              <div className="currency-input">
                <span className="currency-symbol">$</span>
                <input
                  type="text"
                  id="purchasePrice"
                  value={formData.purchasePrice === 0 ? '' : formatCurrency(formData.purchasePrice).replace('$', '')}
                  onChange={(e) => handleValueChange('purchasePrice', e.target.value)}
                  className={`form-input ${errors.purchasePrice ? 'error' : ''}`}
                  placeholder="0"
                />
              </div>
              {errors.purchasePrice && <span className="error-message">{errors.purchasePrice}</span>}
            </div>
          </form>
        </div>
        
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={handleCancel}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleSave}>
            Add Property
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddPropertyModal;
